/**
 * Splide Utilities
 * Reusable utilities for Splide slider functionality across sections
 */

// ========================================================================
// Scrollbar Setup (Draggable)
// ========================================================================
/**
 * Sets up a draggable scrollbar for a Splide instance
 * @param {Object} splideInstance - The Splide instance
 * @param {HTMLElement} container - The section container element
 * @param {Object} options - Optional configuration
 * @param {string} options.scrollbarSelector - Custom selector for scrollbar track (default: '[data-slider-scrollbar]')
 * @param {string} options.thumbSelector - Custom selector for scrollbar thumb (default: '[data-slider-scrollbar-thumb]')
 */
function setupScrollbar(splideInstance, container, options = {}) {
  if (!splideInstance || !container) return;

  const scrollbarSelector = options.scrollbarSelector || '[data-slider-scrollbar]';
  const thumbSelector = options.thumbSelector || '[data-slider-scrollbar-thumb]';

  const scrollbar = container.querySelector(scrollbarSelector);
  const thumb = container.querySelector(thumbSelector);
  if (!scrollbar || !thumb) return;

  let isDragging = false;
  let startX = 0;
  let startThumbLeft = 0;

  // Calculate and update thumb size and position
  const updateScrollbar = () => {
    const perPage = splideInstance.options.perPage || 1;
    const totalSlides = splideInstance.length;
    const currentIndex = splideInstance.index;
    const maxIndex = Math.max(0, totalSlides - perPage);

    // Thumb width as percentage of visible slides
    const thumbWidth = (perPage / totalSlides) * 100;
    thumb.style.width = thumbWidth + '%';

    // Thumb position based on current index
    const thumbPosition = maxIndex > 0 ? (currentIndex / maxIndex) * (100 - thumbWidth) : 0;
    thumb.style.left = thumbPosition + '%';

    // Update ARIA
    const progress = maxIndex > 0 ? Math.round((currentIndex / maxIndex) * 100) : 0;
    scrollbar.setAttribute('aria-valuenow', progress);
  };

  // Get X position from mouse or touch event
  const getClientX = (e) => {
    return e.touches ? e.touches[0].clientX : e.clientX;
  };

  // Calculate target index from position
  const getTargetIndex = (clientX) => {
    const rect = scrollbar.getBoundingClientRect();
    const scrollbarWidth = rect.width;
    const thumbWidth = thumb.offsetWidth;
    const maxIndex = splideInstance.length - (splideInstance.options.perPage || 1);
    const maxThumbPos = scrollbarWidth - thumbWidth;

    const clickX = clientX - rect.left - (thumbWidth / 2);
    const ratio = Math.max(0, Math.min(clickX / maxThumbPos, 1));
    return Math.round(ratio * maxIndex);
  };

  // Drag start handler
  const onDragStart = (e) => {
    isDragging = true;
    startX = getClientX(e);
    startThumbLeft = thumb.offsetLeft;
    document.body.style.userSelect = 'none';
    thumb.classList.add('is-dragging');
    scrollbar.classList.add('is-dragging');
  };

  // Drag move handler
  const onDragMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();

    const deltaX = getClientX(e) - startX;
    const scrollbarWidth = scrollbar.offsetWidth;
    const thumbWidth = thumb.offsetWidth;
    const maxIndex = splideInstance.length - (splideInstance.options.perPage || 1);
    const maxThumbMove = scrollbarWidth - thumbWidth;

    // Calculate target index based on drag position
    const newThumbLeft = Math.max(0, Math.min(startThumbLeft + deltaX, maxThumbMove));
    const ratio = maxThumbMove > 0 ? newThumbLeft / maxThumbMove : 0;
    const targetIndex = Math.round(ratio * maxIndex);

    // Only trigger Splide when we cross into a new slide position
    if (targetIndex !== splideInstance.index) {
      splideInstance.go(targetIndex);
    }
  };

  // Drag end handler
  const onDragEnd = () => {
    if (isDragging) {
      isDragging = false;
      document.body.style.userSelect = '';
      thumb.classList.remove('is-dragging');
      scrollbar.classList.remove('is-dragging');
    }
  };

  // Track click handler (jump to position)
  const onTrackClick = (e) => {
    if (e.target === thumb) return;
    const targetIndex = getTargetIndex(e.clientX);
    splideInstance.go(targetIndex);
  };

  // Keyboard navigation
  const onKeydown = (e) => {
    switch (e.key) {
      case 'ArrowLeft':
        e.preventDefault();
        splideInstance.go('<');
        break;
      case 'ArrowRight':
        e.preventDefault();
        splideInstance.go('>');
        break;
      case 'Home':
        e.preventDefault();
        splideInstance.go(0);
        break;
      case 'End':
        e.preventDefault();
        splideInstance.go(splideInstance.length - 1);
        break;
    }
  };

  // Mouse events
  thumb.addEventListener('mousedown', onDragStart);
  document.addEventListener('mousemove', onDragMove);
  document.addEventListener('mouseup', onDragEnd);

  // Touch events
  thumb.addEventListener('touchstart', onDragStart, { passive: true });
  document.addEventListener('touchmove', onDragMove, { passive: false });
  document.addEventListener('touchend', onDragEnd);

  // Track click
  scrollbar.addEventListener('click', onTrackClick);

  // Keyboard
  scrollbar.addEventListener('keydown', onKeydown);

  // Splide event listeners
  splideInstance.on('move', updateScrollbar);
  splideInstance.on('moved', updateScrollbar);
  splideInstance.on('mounted', updateScrollbar);

  // Initial update
  updateScrollbar();

  // Return cleanup function
  return {
    destroy: () => {
      thumb.removeEventListener('mousedown', onDragStart);
      document.removeEventListener('mousemove', onDragMove);
      document.removeEventListener('mouseup', onDragEnd);
      thumb.removeEventListener('touchstart', onDragStart);
      document.removeEventListener('touchmove', onDragMove);
      document.removeEventListener('touchend', onDragEnd);
      scrollbar.removeEventListener('click', onTrackClick);
      scrollbar.removeEventListener('keydown', onKeydown);
    }
  };
}

// ========================================================================
// Custom Arrows Setup
// ========================================================================
/**
 * Sets up custom navigation arrows for a Splide instance
 * @param {Object} splideInstance - The Splide instance
 * @param {HTMLElement} container - The section container element
 * @param {Object} options - Optional configuration
 * @param {string} options.prevSelector - Custom selector for prev button (default: '.custom-splide-arrows__arrow--prev')
 * @param {string} options.nextSelector - Custom selector for next button (default: '.custom-splide-arrows__arrow--next')
 * @returns {Object} Object with updateArrows function
 */
function setupCustomArrows(splideInstance, container, options = {}) {
  if (!splideInstance || !container) return null;

  const prevSelector = options.prevSelector || '.custom-splide-arrows__arrow--prev';
  const nextSelector = options.nextSelector || '.custom-splide-arrows__arrow--next';

  // Get custom arrow buttons
  const prev = container.querySelector(prevSelector);
  const next = container.querySelector(nextSelector);

  // Update arrow states based on current position
  function updateArrows() {
    const perPage = splideInstance.options.perPage || 1;
    const lastIndex = Math.max(0, splideInstance.length - perPage);
    const isRewind = splideInstance.options.rewind;
    const isLoop = splideInstance.options.type === 'loop';

    if (prev) {
      // If rewind or loop is enabled, prev is always enabled
      prev.disabled = (isRewind || isLoop) ? false : splideInstance.index === 0;
    }
    if (next) {
      // If rewind or loop is enabled, next is always enabled
      next.disabled = (isRewind || isLoop) ? false : splideInstance.index >= lastIndex;
    }
  }

  // Connect custom arrows to Splide
  if (prev) {
    prev.addEventListener('click', function () {
      splideInstance.go('<');
    });
  }

  if (next) {
    next.addEventListener('click', function () {
      splideInstance.go('>');
    });
  }

  // Listen for slide changes
  splideInstance.on('move', updateArrows);
  splideInstance.on('moved', updateArrows);
  splideInstance.on('mounted', updateArrows);

  // Initial update - important for arrows to work on page load
  updateArrows();

  return { updateArrows };
}

// ========================================================================
// Combined Setup (Arrows + Scrollbar)
// ========================================================================
/**
 * Sets up both custom arrows and scrollbar for a Splide instance
 * @param {Object} splideInstance - The Splide instance
 * @param {HTMLElement} container - The section container element
 * @param {Object} options - Optional configuration
 * @param {string} options.prevSelector - Custom selector for prev button
 * @param {string} options.nextSelector - Custom selector for next button
 * @param {string} options.scrollbarSelector - Custom selector for scrollbar
 * @param {string} options.thumbSelector - Custom selector for scrollbar thumb
 */
function setupSliderControls(splideInstance, container, options = {}) {
  if (!splideInstance || !container) return null;

  const arrowsSetup = setupCustomArrows(splideInstance, container, {
    prevSelector: options.prevSelector,
    nextSelector: options.nextSelector
  });

  const scrollbarSetup = setupScrollbar(splideInstance, container, {
    scrollbarSelector: options.scrollbarSelector,
    thumbSelector: options.thumbSelector
  });

  return { ...arrowsSetup, ...scrollbarSetup };
}

// ========================================================================
// Tabindex Management for Accessibility
// ========================================================================
/**
 * Sets up comprehensive tabindex management for slider accessibility
 * Manages focus for slides and their focusable descendants, including clones
 * @param {Object} splideInstance - The Splide instance
 * @param {HTMLElement} sliderElement - The slider element
 */
function setupSliderTabindex(splideInstance, sliderElement) {
  if (!splideInstance || !sliderElement) return;

  const updateSlideFocus = () => {
    const slides = sliderElement.querySelectorAll('.splide__slide');
    slides.forEach((slide) => {
      const isHidden = slide.getAttribute('aria-hidden') === 'true';

      // Set tabindex on slide itself
      slide.setAttribute('tabindex', isHidden ? '-1' : '0');

      // Update all potentially focusable descendants
      const focusables = slide.querySelectorAll(
        'a, button, input, textarea, select, details, summary, iframe, [tabindex]:not([tabindex="-1"]), [contenteditable="true"]'
      );

      focusables.forEach((el) => {
        // Skip if this element is the slide itself
        if (el === slide) return;

        if (isHidden) {
          el.setAttribute('tabindex', '-1');
          // Store original tabindex if it exists
          if (!el.hasAttribute('data-original-tabindex') && el.hasAttribute('tabindex')) {
            el.setAttribute('data-original-tabindex', el.getAttribute('tabindex'));
          }
        } else {
          // Restore original tabindex or remove if it was set by us
          if (el.hasAttribute('data-original-tabindex')) {
            el.setAttribute('tabindex', el.getAttribute('data-original-tabindex'));
            el.removeAttribute('data-original-tabindex');
          } else if (el.getAttribute('tabindex') === '-1') {
            el.removeAttribute('tabindex');
          }
        }
      });
    });
  };

  // Listen to all relevant Splide events
  splideInstance.on('mounted move moved refresh updated', updateSlideFocus);

  // Additional safety: use MutationObserver to catch any missed updates
  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === 'attributes' && mutation.attributeName === 'aria-hidden') {
        updateSlideFocus();
      }
    });
  });

  observer.observe(sliderElement, {
    attributes: true,
    attributeFilter: ['aria-hidden'],
    subtree: true
  });
}

// ========================================================================
// Complete Splide Initialization Helper
// ========================================================================
/**
 * Complete initialization helper that sets up a Splide slider with all common features
 * @param {HTMLElement} sliderElement - The slider element
 * @param {HTMLElement} container - The section container element
 * @param {Object} splideOptions - Splide configuration options
 * @param {Object} features - Features to enable
 * @param {boolean} features.customArrows - Enable custom arrows (default: true)
 * @param {boolean} features.scrollbar - Enable scrollbar (default: true)
 * @param {boolean} features.tabindex - Enable tabindex management (default: true)
 * @param {string} features.prevSelector - Custom prev button selector
 * @param {string} features.nextSelector - Custom next button selector
 * @param {string} features.scrollbarSelector - Custom scrollbar selector
 * @param {string} features.thumbSelector - Custom scrollbar thumb selector
 * @returns {Object} The Splide instance
 */
function initSplideWithControls(sliderElement, container, splideOptions = {}, features = {}) {
  if (!sliderElement) return null;

  // Default features
  const enableCustomArrows = features.customArrows !== false;
  const enableScrollbar = features.scrollbar !== false;
  const enableTabindex = features.tabindex !== false;

  // Initialize Splide
  const splideInstance = new Splide(sliderElement, splideOptions);
  splideInstance.mount();

  // Setup custom arrows
  if (enableCustomArrows && container) {
    setupCustomArrows(splideInstance, container, {
      prevSelector: features.prevSelector,
      nextSelector: features.nextSelector
    });
  }

  // Setup scrollbar
  if (enableScrollbar && container) {
    setupScrollbar(splideInstance, container, {
      scrollbarSelector: features.scrollbarSelector,
      thumbSelector: features.thumbSelector
    });
  }

  // Setup tabindex
  if (enableTabindex) {
    setupSliderTabindex(splideInstance, sliderElement);
  }

  return splideInstance;
}

// ========================================================================
// Shopify Theme Editor Events
// ========================================================================
/**
 * Sets up Shopify theme editor event handlers for a section with Splide
 * @param {string} sectionIdPrefix - The prefix of the section ID (e.g., 'ProductFeatures-', 'FeaturedTestimonials-')
 * @param {string} sliderSelector - Selector for the slider element within the section
 * @param {Function} reinitCallback - Optional callback to reinitialize the slider
 */
function setupThemeEditorEvents(sectionIdPrefix, sliderSelector, reinitCallback) {
  if (typeof Shopify === 'undefined' || !Shopify.designMode) return;

  // Section load event
  document.addEventListener('shopify:section:load', (event) => {
    const sliderElement = event.target.querySelector(sliderSelector);
    if (sliderElement && typeof Splide !== 'undefined') {
      if (reinitCallback) {
        reinitCallback(event.target);
      } else {
        // Simple reload for theme editor if no callback provided
        location.reload();
      }
    }
  });

  // Section unload event
  document.addEventListener('shopify:section:unload', (event) => {
    const container = event.target.querySelector(`[id^="${sectionIdPrefix}"]`);
    if (container && container.sliderInstance) {
      container.sliderInstance.destroy();
    }
  });

  // Section select event
  document.addEventListener('shopify:section:select', (event) => {
    const container = event.target.querySelector(`[id^="${sectionIdPrefix}"]`);
    if (container && reinitCallback) {
      reinitCallback(event.target);
    }
  });

  // Section deselect event
  document.addEventListener('shopify:section:deselect', (event) => {
    const container = event.target.querySelector(`[id^="${sectionIdPrefix}"]`);
    if (container && container.sliderInstance) {
      // Optional: handle deselect if needed
    }
  });

  // Block select event
  document.addEventListener('shopify:block:select', (event) => {
    const container = event.target.closest(`[id^="${sectionIdPrefix}"]`);
    if (container && container.sliderInstance && container.sliderInstance.splideInstance) {
      const blockIndex = Array.from(event.target.parentElement.children).indexOf(event.target);
      container.sliderInstance.splideInstance.go(blockIndex);
    }
  });
}

// ========================================================================
// Export for use in other files
// ========================================================================
if (typeof window !== 'undefined') {
  window.SplideUtils = {
    setupScrollbar,
    setupCustomArrows,
    setupSliderControls,
    setupSliderTabindex,
    initSplideWithControls,
    setupThemeEditorEvents
  };
}
